import React, { FC, useEffect, useState } from 'react'
import { Alert, Button, Input, Modal, notification, Table, Typography } from 'antd'
import { ColumnsType } from 'antd/lib/table'
import Icon from '../../components/Icon'
import { storageApi } from '../../api'
import { GenerationTypeKey, GENERATION_TYPES, IPlant, IPlantForm } from '../../types/plant'
import PlantForm from '../New/PlantForm'
import moment from 'moment'

const Plants: FC = () => {
  const [isLoading, setIsLoading] = useState(true)
  const [isEditModalVisible, setIsEditModalVisible] = useState(false)
  const [isEditLoading, setIsEditLoading] = useState(false)
  const [selectedPlant, setSelectedPlant] = useState<IPlantForm & { id: string }>()
  const [plants, setPlants] = useState<IPlant[]>([])

  async function fetchPlants() {
    try {
      setIsLoading(true)
      const plantsResponse = await storageApi.fetchPlants()
      const validatedPlants = plantsResponse.map((plant) => {
        if (!Object.keys(GENERATION_TYPES).includes(plant.generationType)) {
          return { ...plant, generationType: GENERATION_TYPES.OTHER.value }
        }
        return plant
      })
      setPlants(validatedPlants)
      setIsLoading(false)
    } catch {
      setPlants([])
    }
  }
  function editPlant(plant: IPlant) {
    setSelectedPlant({ id: plant.id, ...mapPlantForEditForm(plant) })
    setIsEditModalVisible(true)
  }
  function onPlantFieldChange(_: any, editedPlants: { plants: [IPlantForm] }) {
    if (!selectedPlant) return
    const [editedPlant] = editedPlants.plants
    setSelectedPlant({ id: selectedPlant.id, ...editedPlant })
  }
  async function savePlant() {
    try {
      if (selectedPlant) {
        setIsEditLoading(true)
        const plantId = selectedPlant.id
        const fileUrl = selectedPlant.generationFile?.[0]?.url || ''
        const feedInTariffDollarsEndDate =
          selectedPlant.feedInTariffDollarsEndDate?.toISOString() || null
        const plant = { ...selectedPlant, url: fileUrl, feedInTariffDollarsEndDate }

        await storageApi.editPlant(plantId, plant)
        notification.success({ message: 'Plant has been edited successfully' })
        setIsEditModalVisible(false)
      }
    } finally {
      setIsEditLoading(false)
      fetchPlants()
    }
  }
  async function deletePlant(plantId: string) {
    setIsLoading(true)
    await storageApi.deletePlant(plantId)
    fetchPlants()
  }

  function mapPlantForEditForm(plant: IPlant): IPlantForm {
    return {
      ...plant,
      feedInTariffDollarsEndDate: plant.feedInTariffDollarsEndDate
        ? moment(plant.feedInTariffDollarsEndDate)
        : null,
      generationFile: [{ uid: plant.id, name: plant.url, url: plant.url }],
    }
  }

  useEffect(() => {
    fetchPlants()
  }, [])

  const plantsTableColumns: ColumnsType<IPlant> = [
    {
      title: 'Type',
      dataIndex: 'generationType',
      key: 'generationType',
      align: 'center',
      render: (type: GenerationTypeKey) => (
        <Icon icon={GENERATION_TYPES[type].icon} color={GENERATION_TYPES[type].color} />
      ),
    },
    {
      title: 'Name',
      dataIndex: 'name',
      key: 'name',
      sorter: (a, b) => a.name.length - b.name.length,
    },
    {
      title: 'Ins. Power',
      dataIndex: 'installedPowerKw',
      key: 'installedPowerKw',
      sorter: (a, b) => a.installedPowerKw - b.installedPowerKw,
    },
    {
      title: 'Lens. Power',
      dataIndex: 'licensedPowerKw',
      key: 'licensedPowerKw ',
      sorter: (a, b) => a.licensedPowerKw - b.licensedPowerKw,
    },
    {
      title: 'Created Date',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (date: string) => new Date(date).toLocaleString(),
      sorter: (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
    },
    {
      title: 'Created By',
      dataIndex: 'createdBy',
      key: 'createdBy',
    },
    {
      title: '',
      dataIndex: 'actions',
      key: 'actions',
      align: 'center',
      render: (_, plant: IPlant) => (
        <div>
          <Button
            className='mr-8'
            icon={<Icon icon='pen-to-square' margin={false} />}
            onClick={() => editPlant(plant)}
          />
          <Button
            icon={<Icon icon='trash-alt' margin={false} />}
            danger
            onClick={() => deletePlant(plant.id)}
          />
        </div>
      ),
    },
  ]

  return (
    <>
      <Alert
        type='info'
        message={<Typography.Title level={4}>Plants</Typography.Title>}
        description='Wind, PV or Diesel Generator power plants that can be combined with a storage plant. You can edit previously created plant variables from here.'
      />

      <Input.Search className='my-24' placeholder='Search' onChange={fetchPlants} />
      <Table
        rowKey='id'
        columns={plantsTableColumns}
        dataSource={plants}
        loading={isLoading}
        bordered
      />

      <Modal
        title={selectedPlant?.name}
        visible={isEditModalVisible}
        confirmLoading={isEditLoading}
        width={1200}
        okText='Save'
        closable={false}
        destroyOnClose
        onCancel={() => setIsEditModalVisible(false)}
        onOk={savePlant}
      >
        {selectedPlant && (
          <PlantForm
            initialValues={{
              plants: [selectedPlant],
            }}
            onValuesChange={onPlantFieldChange}
          />
        )}
      </Modal>
    </>
  )
}

export default Plants
